import { useState, useCallback, useEffect } from 'react';
import logger from '../lib/logger';
import { useAuth } from '../context/AuthContext';
import { reportsAPI } from '../lib/api';
import { formatCurrency, formatDateTime, formatDate, paymentMethodLabels, cn } from '../lib/utils';
import { exportToExcel, exportToPDF } from '../lib/exportUtils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  DollarSign, TrendingUp, TrendingDown, Wallet, Loader2, FileSpreadsheet, FileText, ArrowLeft, HandCoins,
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const _today = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Mexico_City' });

const _rangeToday = () => ({ start: _today(), end: _today() });

const _rangeWeek = () => {
  const now = new Date();
  const day = now.getDay(); // 0..6, Sun..Sat
  const diffToMon = day === 0 ? -6 : 1 - day;
  const start = new Date(now);
  start.setDate(now.getDate() + diffToMon);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  const fmt = (d) => d.toLocaleDateString('en-CA', { timeZone: 'America/Mexico_City' });
  return { start: fmt(start), end: fmt(end) };
};

const _rangeMonth = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const fmt = (d) => d.toLocaleDateString('en-CA', { timeZone: 'America/Mexico_City' });
  return { start: fmt(start), end: fmt(end) };
};

// Spanish month abbreviations used for compact PDF dates.
const _MES_ABREV = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const _MES_NOMBRE = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

// Currency helper — currency values are already formatted as "$13,140.00" by
// formatCurrency(). Negatives already come as "-$292.00".
const cur = (v) => formatCurrency(Number(v || 0));

// "8 sep 2026"
const _formatDateShortES = (input) => {
  if (!input) return '';
  const d = typeof input === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(input)
    ? new Date(input + 'T12:00:00')  // avoid TZ shift for pure date strings
    : new Date(input);
  if (Number.isNaN(d.getTime())) return String(input);
  return `${d.getDate()} ${_MES_ABREV[d.getMonth()]} ${d.getFullYear()}`;
};

// "7 sep 2026, 7:57 p. m."
const _formatDateTimeLongES = (input) => {
  if (!input) return '';
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return String(input);
  const opts = { timeZone: 'America/Mexico_City', hour: 'numeric', minute: '2-digit', hour12: true };
  const time = d.toLocaleTimeString('es-MX', opts).replace(/\s?a\.?\s?m\.?/i, 'a. m.').replace(/\s?p\.?\s?m\.?/i, 'p. m.');
  // Compose the date part in CDMX
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Mexico_City', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(d);
  const y = parts.find((p) => p.type === 'year').value;
  const m = Number(parts.find((p) => p.type === 'month').value) - 1;
  const day = Number(parts.find((p) => p.type === 'day').value);
  return `${day} ${_MES_ABREV[m]} ${y}, ${time}`;
};

// "Del 7 al 13 de septiembre de 2026" (with mixed-month fallback)
const _formatPeriodES = (start, end) => {
  if (!start || !end) return '';
  const s = new Date(start + 'T12:00:00');
  const e = new Date(end + 'T12:00:00');
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return `${start} a ${end}`;
  const sameMonth = s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear();
  if (sameMonth) {
    if (s.getDate() === e.getDate()) return `${s.getDate()} de ${_MES_NOMBRE[s.getMonth()]} de ${s.getFullYear()}`;
    return `Del ${s.getDate()} al ${e.getDate()} de ${_MES_NOMBRE[s.getMonth()]} de ${s.getFullYear()}`;
  }
  return `Del ${s.getDate()} de ${_MES_NOMBRE[s.getMonth()]} al ${e.getDate()} de ${_MES_NOMBRE[e.getMonth()]} de ${e.getFullYear()}`;
};

// "7-13 SEP 2026" — used in the page-2 compact header.
const _rangeShortES = (start, end) => {
  if (!start || !end) return '';
  const s = new Date(start + 'T12:00:00');
  const e = new Date(end + 'T12:00:00');
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return '';
  const month = _MES_ABREV[e.getMonth()].toUpperCase();
  if (s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear()) {
    return `${s.getDate()}-${e.getDate()} ${month} ${e.getFullYear()}`;
  }
  return `${s.getDate()} ${_MES_ABREV[s.getMonth()].toUpperCase()} - ${e.getDate()} ${month} ${e.getFullYear()}`;
};

// Human labels for expense categories stored internally as English slugs.
const _EXPENSE_CATEGORY_LABELS = {
  utilities: 'Servicios',
  supplies: 'Insumos',
  other: 'Otros',
  rent: 'Renta',
  payroll: 'Nómina',
  marketing: 'Publicidad',
  maintenance: 'Mantenimiento',
  taxes: 'Impuestos',
};

export default function ReporteCajaDetalladoPage() {
  const { selectedBusiness } = useAuth();
  const [range, setRange] = useState(_rangeToday());
  const [preset, setPreset] = useState('today');
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);

  const load = useCallback(async () => {
    if (!selectedBusiness) return;
    setLoading(true);
    try {
      const { data } = await reportsAPI.cashDetailed(range.start, range.end);
      setReport(data);
    } catch (err) {
      logger.error('Cash detailed', err);
      toast.error('Error al cargar el reporte');
    } finally {
      setLoading(false);
    }
  }, [selectedBusiness, range.start, range.end]);

  useEffect(() => { load(); }, [load]);

  const handlePreset = (p) => {
    setPreset(p);
    if (p === 'today') setRange(_rangeToday());
    else if (p === 'week') setRange(_rangeWeek());
    else if (p === 'month') setRange(_rangeMonth());
  };

  const s = report?.summary || {};
  const bizName = selectedBusiness?.name || 'Barbería';
  const periodLabel = _formatPeriodES(range.start, range.end);
  const rangeLabel = _rangeShortES(range.start, range.end);
  const presetTitle = preset === 'today' ? 'CIERRE DIARIO' : preset === 'week' ? 'CIERRE SEMANAL' : preset === 'month' ? 'CIERRE MENSUAL' : 'CIERRE PERSONALIZADO';

  const buildExportPayload = () => {
    const sales = (report?.sales || []).map((x) => ({
      fecha: formatDateTime(x.created_at),
      ticket: x.ticket_number || (x.id || '').slice(0, 8),
      cliente: x.client_name || 'Sin cliente',
      tipo: x.appointment_id ? 'Con cita' : 'Walk-in',
      metodo: paymentMethodLabels[x.payment_method] || x.payment_method,
      total: Number(x.total || 0).toFixed(2),
    }));
    const expenses = (report?.expenses || []).map((x) => ({
      fecha: _formatDateShortES(x.date),
      categoria: _EXPENSE_CATEGORY_LABELS[x.category] || x.category || '—',
      concepto: (x.description || '').toUpperCase(),
      monto: cur(x.amount || 0),
    }));
    // Retiros de caja (cash_movements filtered to withdrawals). Deposits and
    // adjustments are intentionally left out — they don't reduce the day's
    // liquid cash, they either add to it or reconcile it.
    const withdrawals = (report?.cash_movements || [])
      .filter((m) => m.type === 'withdrawal')
      .map((x) => ({
        fecha: _formatDateTimeLongES(x.created_at),
        concepto: x.description || 'Retiro de caja',
        monto: cur(Math.abs(x.amount || 0)),
      }));
    const commissions = (report?.commissions_paid || []).map((x) => ({
      fecha: formatDate(x.paid_at || x.created_at),
      barbero: x.barber_name || '—',
      concepto: x.sale_item_name || 'Comisión',
      monto: Number(x.commission_amount || 0).toFixed(2),
    }));
    const tips = (report?.tips_paid || []).map((x) => ({
      fecha: formatDate(x.tip_paid_at || x.created_at),
      barbero: x.barber_name || '—',
      cliente: x.client_name || 'Sin cliente',
      ticket: x.ticket_number || (x.id || '').slice(0, 8),
      monto: Number(x.tip_amount || 0).toFixed(2),
    }));
    return { sales, expenses, withdrawals, commissions, tips };
  };

  const handleExportExcel = () => {
    if (!report) return;
    const { sales, expenses, withdrawals, commissions, tips } = buildExportPayload();
    exportToExcel(`Reporte_Caja_${range.start}_a_${range.end}`, [
      {
        name: 'Resumen',
        columns: [
          { header: 'Concepto', key: 'concepto' },
          { header: 'Monto', key: 'monto' },
        ],
        rows: [
          { concepto: 'Total ventas', monto: s.total_sales?.toFixed(2) },
          { concepto: '  Efectivo', monto: s.sales_by_method?.cash?.toFixed(2) },
          { concepto: '  Tarjeta', monto: s.sales_by_method?.card?.toFixed(2) },
          { concepto: '  Transferencia', monto: s.sales_by_method?.transfer?.toFixed(2) },
          { concepto: 'Total gastos', monto: s.total_expenses?.toFixed(2) },
          { concepto: 'Comisiones pagadas', monto: s.total_commissions_paid?.toFixed(2) },
          { concepto: 'Propinas pagadas', monto: s.total_tips_paid?.toFixed(2) },
          { concepto: 'Utilidad neta', monto: s.net_profit?.toFixed(2) },
        ],
      },
      {
        name: 'Ventas',
        columns: [
          { header: 'Fecha', key: 'fecha' },
          { header: 'Ticket', key: 'ticket' },
          { header: 'Cliente', key: 'cliente' },
          { header: 'Tipo', key: 'tipo' },
          { header: 'Método', key: 'metodo' },
          { header: 'Total', key: 'total' },
        ],
        rows: sales,
      },
      {
        name: 'Gastos',
        columns: [
          { header: 'Fecha', key: 'fecha' },
          { header: 'Categoría', key: 'categoria' },
          { header: 'Concepto', key: 'concepto' },
          { header: 'Monto', key: 'monto' },
        ],
        rows: expenses,
      },
      {
        name: 'Retiros de caja',
        columns: [
          { header: 'Fecha', key: 'fecha' },
          { header: 'Concepto', key: 'concepto' },
          { header: 'Monto', key: 'monto' },
        ],
        rows: withdrawals,
      },
      {
        name: 'Comisiones',
        columns: [
          { header: 'Fecha', key: 'fecha' },
          { header: 'Barbero', key: 'barbero' },
          { header: 'Concepto', key: 'concepto' },
          { header: 'Monto', key: 'monto' },
        ],
        rows: commissions,
      },
      {
        name: 'Propinas pagadas',
        columns: [
          { header: 'Fecha pago', key: 'fecha' },
          { header: 'Barbero', key: 'barbero' },
          { header: 'Cliente', key: 'cliente' },
          { header: 'Ticket', key: 'ticket' },
          { header: 'Monto', key: 'monto' },
        ],
        rows: tips,
      },
    ]);
    toast.success('Excel descargado');
  };

  const handleExportPDF = () => {
    if (!report) return;
    const { expenses, withdrawals } = buildExportPayload();
    const sumario = report?.sumario || {};
    const methodLabels = { cash: 'Efectivo', card: 'Tarjeta', transfer: 'Transferencia', other: 'Otros' };

    // KPI row — the 4 headline numbers.
    const totalRecibido = sumario.total_recibido || 0;
    const propinas = sumario.total_tips_collected || 0;
    const recibidoNeto = sumario.recibido_neto || 0;
    const gastosTotal = sumario.gastos?.total || 0;
    const withdrawalsTotal = withdrawals.reduce((sum, w) => {
      const raw = String(w.monto || '').replace(/[^0-9.-]/g, '');
      return sum + (parseFloat(raw) || 0);
    }, 0);
    const totalSalidas = gastosTotal + withdrawalsTotal;

    const kpis = [
      { label: 'Total recibido', value: cur(totalRecibido) },
      { label: 'Propinas', value: `-${cur(propinas)}` },
      { label: 'Recibido neto', value: cur(recibidoNeto) },
      { label: 'Total salidas', value: cur(totalSalidas) },
    ];

    // 01 SUMARIO DE MOVIMIENTOS — 2 groups
    const metodosPagoGroup = {
      subtitle: 'Métodos de pago',
      items: (sumario.recaudaciones || []).map((r) => ({
        label: methodLabels[r.method] || r.method,
        value: cur(r.amount),
      })),
    };
    const conciliacionGroup = {
      subtitle: 'Conciliación',
      items: [
        { label: 'Total recibido', value: cur(totalRecibido) },
        { label: 'Menos propinas', value: `-${cur(propinas)}` },
        { label: 'Recibido neto', value: cur(recibidoNeto), isTotal: true },
      ],
    };

    // 02 ESTADÍSTICAS — as a numeric table (indicator/promedio/tickets/total)
    const mt = sumario.media_ticket || {};
    const estadisticasRows = [
      { indicador: 'Servicio', promedio: cur(mt.service?.average), tickets: mt.service?.count || 0, total: cur(mt.service?.total) },
      { indicador: 'Producto', promedio: cur(mt.product?.average), tickets: mt.product?.count || 0, total: cur(mt.product?.total) },
      { indicador: 'Ticket general', promedio: cur(mt.overall?.average), tickets: mt.overall?.count || 0, total: cur(mt.overall?.total || recibidoNeto) },
    ];

    // 03 VENTAS Y SALIDAS — combine ventas + gastos/retiros
    const v = sumario.ventas || {};
    const ventasGroup = {
      subtitle: 'Ventas',
      items: [
        { label: 'Servicios', value: cur(v.service?.amount) },
        { label: 'Productos', value: cur(v.product?.amount) },
        { label: 'Total de ventas', value: cur(v.total), isTotal: true },
      ],
    };
    const salidasGroup = {
      subtitle: 'Gastos y retiros',
      items: [
        { label: 'Otros gastos', hint: `${sumario.gastos?.count || 0} registros`, value: cur(gastosTotal) },
        { label: 'Retiros de caja', hint: `${withdrawals.length} registros`, value: cur(withdrawalsTotal) },
        { label: 'Total de salidas', value: cur(totalSalidas), isTotal: true },
      ],
    };

    // 04 PASIVOS
    const p = sumario.pasivos || {};
    const pasivosGroup = {
      items: [
        { label: 'Nuevos inscritos al programa de lealtad', value: String(p.new_loyalty_members || 0) },
        { label: 'Canjes descuento parcial', value: String(p.redeem_50 || 0) },
        { label: 'Canjes servicio gratis', value: String(p.redeem_free || 0) },
        { label: 'Descuentos aplicados', value: cur(p.discounts_amount) },
      ],
    };

    // 05 CLIENTES
    const cl = sumario.clientes || {};
    const clientRows = [
      { tipo: 'Con cita (agendados)', clientes: cl.with_appointment?.clients || 0, visitas: cl.with_appointment?.visits || 0, valor: cur(cl.with_appointment?.revenue || 0) },
      { tipo: 'Sin cita (registrados)', clientes: cl.without_appointment?.clients || 0, visitas: cl.without_appointment?.visits || 0, valor: cur(cl.without_appointment?.revenue || 0) },
      { tipo: 'Sin datos', clientes: cl.no_data?.clients || 0, visitas: cl.no_data?.visits || 0, valor: cur(cl.no_data?.revenue || 0) },
      { tipo: 'Nuevos registros', clientes: cl.new_registrations || 0, visitas: '—', valor: '—' },
      { tipo: 'TOTAL', clientes: cl.totals?.clients || 0, visitas: cl.totals?.visits || 0, valor: cur(cl.totals?.revenue || 0) },
    ];

    // 06 DESEMPEÑO POR EMPLEADO — with better column labels
    const empRows = (sumario.empleados || []).map((e) => ({
      empleado: e.display_name,
      tickets: e.tickets,
      servicios: cur(e.services_revenue),
      productos: cur(e.products_revenue),
      total: cur(e.total),
      ticket_promedio: cur(e.ticket_avg),
      serv_por_hora: (e.services_per_hour ?? 0).toFixed ? Number(e.services_per_hour || 0).toFixed(3) : String(e.services_per_hour || 0),
      horas_trabajadas: Number(e.hours_worked || 0).toFixed(1),
    }));
    empRows.push({
      empleado: 'TOTAL',
      tickets: '',
      servicios: cur(v.service?.amount || 0),
      productos: cur(v.product?.amount || 0),
      total: cur(sumario.empleados_total || v.total || 0),
      ticket_promedio: '',
      serv_por_hora: '',
      horas_trabajadas: Number(sumario.empleados_total_hours || 0).toFixed(1),
    });

    exportToPDF(`Reporte_Caja_${range.start}_a_${range.end}`, {
      businessName: bizName,
      title: 'Reporte de caja',
      periodLabel,
      rightSubtitle: presetTitle,
      // rightMeta will default to the generation timestamp inside exportUtils
      kpis,
      sections: [
        {
          number: '01',
          title: 'Sumario de movimientos',
          groups: [metodosPagoGroup, conciliacionGroup],
        },
        {
          number: '02',
          title: 'Estadísticas',
          columns: [
            { header: 'Indicador', key: 'indicador' },
            { header: 'Promedio', key: 'promedio' },
            { header: 'Tickets', key: 'tickets' },
            { header: 'Total', key: 'total' },
          ],
          rows: estadisticasRows,
        },
        {
          number: '03',
          title: 'Ventas y salidas',
          groups: [ventasGroup, salidasGroup],
        },
        {
          number: '04',
          title: 'Pasivos',
          groups: [pasivosGroup],
        },
        {
          number: '05',
          title: 'Clientes',
          columns: [
            { header: 'Tipo', key: 'tipo' },
            { header: 'Clientes', key: 'clientes' },
            { header: 'Visitas', key: 'visitas' },
            { header: 'Valor', key: 'valor' },
          ],
          rows: clientRows,
        },
        {
          number: '06',
          title: 'Desempeño por empleado',
          columns: [
            { header: 'Empleado', key: 'empleado', width: 28 },
            { header: 'Tickets', key: 'tickets', width: 16 },
            { header: 'Servicios', key: 'servicios', width: 20 },
            { header: 'Productos', key: 'productos', width: 20 },
            { header: 'Total', key: 'total', width: 22 },
            { header: 'Ticket promedio', key: 'ticket_promedio', width: 22 },
            { header: 'Servicios por hora', key: 'serv_por_hora', width: 22 },
            { header: 'Horas trabajadas', key: 'horas_trabajadas', width: 22 },
          ],
          rows: empRows,
          note: 'Nota: los tickets por empleado pueden incluir participación compartida; el total general corresponde a los tickets únicos del período.',
        },
        expenses.length > 0 && {
          number: '07',
          title: `Detalle de gastos (${expenses.length})`,
          columns: [
            { header: 'Fecha', key: 'fecha' },
            { header: 'Categoría', key: 'categoria' },
            { header: 'Concepto', key: 'concepto' },
            { header: 'Monto', key: 'monto' },
          ],
          rows: expenses,
        },
        withdrawals.length > 0 && {
          number: '08',
          title: `Detalle de retiros (${withdrawals.length})`,
          columns: [
            { header: 'Fecha', key: 'fecha' },
            { header: 'Concepto', key: 'concepto' },
            { header: 'Monto', key: 'monto' },
          ],
          rows: withdrawals,
        },
      ].filter(Boolean),
    });
    toast.success('PDF descargado');
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="cash-detailed-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <Link to="/reportes" className="text-xs text-slate-500 hover:text-primary inline-flex items-center gap-1 mb-1">
            <ArrowLeft className="w-3 h-3" /> Volver a Reportes
          </Link>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
            Reporte de Caja detallado
          </h1>
          <p className="text-slate-500 mt-1">{bizName}</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleExportExcel}
            disabled={!report}
            className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
            data-testid="export-excel-btn"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel
          </Button>
          <Button
            variant="outline"
            onClick={handleExportPDF}
            disabled={!report}
            className="border-red-200 text-red-700 hover:bg-red-50"
            data-testid="export-pdf-btn"
          >
            <FileText className="w-4 h-4 mr-2" /> PDF
          </Button>
        </div>
      </div>

      {/* Range filters */}
      <Card className="border-slate-200 bg-white">
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <div className="flex gap-1">
            {[
              { v: 'today', label: 'Hoy' },
              { v: 'week', label: 'Esta semana' },
              { v: 'month', label: 'Este mes' },
            ].map((p) => (
              <Button
                key={p.v}
                size="sm"
                variant={preset === p.v ? 'default' : 'outline'}
                className={preset === p.v ? '' : 'border-slate-200 text-slate-600'}
                onClick={() => handlePreset(p.v)}
                data-testid={`preset-${p.v}`}
              >
                {p.label}
              </Button>
            ))}
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <Label className="text-xs text-slate-500">Desde</Label>
            <Input type="date" value={range.start}
              onChange={(e) => { setRange((r) => ({ ...r, start: e.target.value })); setPreset('custom'); }}
              className="bg-slate-100 border-slate-200 h-9 w-40" data-testid="range-start" />
            <Label className="text-xs text-slate-500">Hasta</Label>
            <Input type="date" value={range.end}
              onChange={(e) => { setRange((r) => ({ ...r, end: e.target.value })); setPreset('custom'); }}
              className="bg-slate-100 border-slate-200 h-9 w-40" data-testid="range-end" />
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <Card className="border-slate-200 bg-white">
          <CardContent className="p-8 flex items-center justify-center text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando reporte…
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <StatCard label="Ventas" value={formatCurrency(s.total_sales)} sub={`${s.sales_count || 0} tickets`} color="emerald" icon={TrendingUp} testId="stat-sales" />
            <StatCard label="Gastos" value={formatCurrency(s.total_expenses)} sub={`${s.expenses_count || 0} registros`} color="red" icon={TrendingDown} testId="stat-expenses" />
            <StatCard label="Comisiones pagadas" value={formatCurrency(s.total_commissions_paid)} sub={`${s.commissions_paid_count || 0} pagos`} color="amber" icon={Wallet} testId="stat-commissions-paid" />
            <StatCard label="Propinas pagadas" value={formatCurrency(s.total_tips_paid)} sub={`${s.tips_paid_count || 0} pagos`} color="sky" icon={HandCoins} testId="stat-tips-paid" />
            <StatCard label="Utilidad neta" value={formatCurrency(s.net_profit)} sub="Ventas − gastos − comis − propinas" color={s.net_profit >= 0 ? 'blue' : 'red'} icon={DollarSign} testId="stat-net-profit" />
          </div>

          {/* Payment methods breakdown */}
          <Card className="border-slate-200 bg-white">
            <CardHeader>
              <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>Desglose por método de pago</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-3 gap-4">
              <MethodBar label="Efectivo" value={s.sales_by_method?.cash || 0} total={s.total_sales || 1} color="emerald" />
              <MethodBar label="Tarjeta" value={s.sales_by_method?.card || 0} total={s.total_sales || 1} color="blue" />
              <MethodBar label="Transferencia" value={s.sales_by_method?.transfer || 0} total={s.total_sales || 1} color="violet" />
            </CardContent>
          </Card>

          {/* Sales table */}
          <SectionTable
            title={`Ventas (${report?.sales?.length || 0})`}
            emptyLabel="Sin ventas en este período"
            columns={['Fecha', 'Ticket', 'Cliente', 'Método', 'Total']}
            rows={(report?.sales || []).map((x) => [
              formatDateTime(x.created_at),
              x.ticket_number || (x.id || '').slice(0, 8),
              x.client_name || 'Sin cliente',
              paymentMethodLabels[x.payment_method] || x.payment_method,
              formatCurrency(x.total),
            ])}
          />

          {/* Expenses */}
          <SectionTable
            title={`Gastos (${report?.expenses?.length || 0})`}
            emptyLabel="Sin gastos en este período"
            columns={['Fecha', 'Categoría', 'Concepto', 'Monto']}
            rows={(report?.expenses || []).map((x) => [
              formatDate(x.date),
              x.category || '—',
              x.description || '',
              formatCurrency(x.amount),
            ])}
          />

          {/* Commissions */}
          <SectionTable
            title={`Comisiones / nóminas pagadas (${report?.commissions_paid?.length || 0})`}
            emptyLabel="Sin comisiones pagadas en este período"
            columns={['Fecha', 'Barbero', 'Concepto', 'Monto']}
            rows={(report?.commissions_paid || []).map((x) => [
              formatDate(x.paid_at || x.created_at),
              x.barber_name || '—',
              x.sale_item_name || 'Comisión',
              formatCurrency(x.commission_amount),
            ])}
          />

          {/* Tips paid */}
          <SectionTable
            title={`Propinas pagadas (${report?.tips_paid?.length || 0})`}
            emptyLabel="Sin propinas pagadas en este período"
            columns={['Fecha pago', 'Barbero', 'Cliente', 'Ticket', 'Monto']}
            rows={(report?.tips_paid || []).map((x) => [
              formatDate(x.tip_paid_at || x.created_at),
              x.barber_name || '—',
              x.client_name || 'Sin cliente',
              x.ticket_number || (x.id || '').slice(0, 8),
              formatCurrency(x.tip_amount),
            ])}
          />
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, sub, color, icon: Icon, testId }) {
  const map = {
    emerald: 'text-emerald-600 bg-emerald-50',
    red: 'text-red-600 bg-red-50',
    amber: 'text-amber-600 bg-amber-50',
    blue: 'text-blue-600 bg-blue-50',
    sky: 'text-sky-600 bg-sky-50',
  };
  return (
    <Card className="border-slate-200 bg-white" data-testid={testId}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
          <div className={cn('w-8 h-8 rounded flex items-center justify-center', map[color])}>
            <Icon className="w-4 h-4" />
          </div>
        </div>
        <p className="text-2xl font-bold mt-2" style={{ fontFamily: 'Manrope' }}>{value}</p>
        <p className="text-xs text-slate-500 mt-1">{sub}</p>
      </CardContent>
    </Card>
  );
}

function MethodBar({ label, value, total, color }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  const map = {
    emerald: 'bg-emerald-500', blue: 'bg-blue-500', violet: 'bg-violet-500',
  };
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-slate-500">{label}</span>
        <span className="font-medium">{formatCurrency(value)} · {pct}%</span>
      </div>
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className={cn('h-full', map[color])} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function SectionTable({ title, emptyLabel, columns, rows }) {
  return (
    <Card className="border-slate-200 bg-white">
      <CardHeader>
        <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="text-sm text-slate-400 py-4 text-center">{emptyLabel}</p>
        ) : (
          <div className="max-h-96 overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {columns.map((h) => (
                    <TableHead key={h} className={h === 'Total' || h === 'Monto' ? 'text-right' : ''}>{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r, i) => (
                  <TableRow key={i}>
                    {r.map((cell, j) => (
                      <TableCell key={j} className={j === r.length - 1 ? 'text-right font-medium' : 'text-sm'}>
                        {cell}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
